import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { cn } from '@/shared/lib';
import type { StatusTone } from './StatusPill';
import { Tooltip } from './Tooltip';

export interface ListRowAction {
  key: string;
  label: string;
  onClick?: () => void;
  /** Переход (карточка связанной записи) вместо действия. */
  href?: string;
  /** Опасное действие («Удалить», «Снять») — цвет status-error. */
  danger?: boolean;
  disabled?: boolean;
  /** Почему действие недоступно (подсказка у неактивной ссылки). */
  disabledReason?: string;
}

export interface ListRowItem {
  key: string;
  title: ReactNode;
  /** Вторая строка (12 ink-600). */
  text?: ReactNode;
  /** Третья строка (12 ink-500). */
  meta?: ReactNode;
  tag?: { label: string; tone: StatusTone };
  actions?: readonly ListRowAction[];
}

const TAG_TONES: Readonly<Record<StatusTone, string>> = {
  ok: 'bg-tone-ok-bg text-tone-ok-fg',
  warn: 'bg-tone-warn-bg text-tone-warn-fg',
  bad: 'bg-tone-bad-bg text-tone-bad-fg',
  mute: 'bg-tone-mute-bg text-tone-mute-fg',
  gold: 'bg-tone-gold-bg text-tone-gold-fg',
};

const ACTION =
  'shrink-0 cursor-pointer border-0 bg-transparent p-0 text-[12px] font-medium whitespace-nowrap no-underline hover:underline disabled:cursor-not-allowed disabled:text-ink-400 disabled:no-underline';

function RowAction({ action }: { action: ListRowAction }) {
  const tone = action.danger ? 'text-status-error' : 'text-wine-bordeaux';
  if (action.href && !action.disabled) {
    return (
      <Link to={action.href} className={cn(ACTION, tone)}>
        {action.label}
      </Link>
    );
  }
  const button = (
    <button
      type="button"
      onClick={action.onClick}
      disabled={action.disabled}
      className={cn(ACTION, action.disabled ? undefined : tone)}
    >
      {action.label}
    </button>
  );
  if (action.disabled && action.disabledReason) {
    return (
      <Tooltip content={action.disabledReason}>
        <span className="inline-flex shrink-0">{button}</span>
      </Tooltip>
    );
  }
  return button;
}

/**
 * Строки списка карточки (прототип, блок `list`): padding 9 14, разделитель line-soft; название
 * 13/500 ink-900, текст 12 ink-600, мета 12 ink-500; метка — 22, radius 12, 11/500 цветом тона;
 * действия строки — 12/500 (wine-bordeaux, опасные — status-error).
 */
export function ListRows({ items }: { items: readonly ListRowItem[] }) {
  return (
    <ul className="m-0 flex list-none flex-col p-0">
      {items.map((item) => (
        <li
          key={item.key}
          className="flex items-center gap-14 border-t border-line-soft px-14 py-9 first:border-t-0"
        >
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <span className="text-[13px] font-medium text-pretty text-ink-900">{item.title}</span>
            {item.text ? (
              <span className="text-[12px] [overflow-wrap:anywhere] text-pretty text-ink-600">
                {item.text}
              </span>
            ) : null}
            {item.meta ? <span className="text-[12px] text-ink-500">{item.meta}</span> : null}
          </div>
          {item.tag ? (
            <span
              className={cn(
                'inline-flex h-22 shrink-0 items-center rounded-12 px-8 text-[11px] font-medium whitespace-nowrap',
                TAG_TONES[item.tag.tone],
              )}
            >
              {item.tag.label}
            </span>
          ) : null}
          {item.actions?.map((action) => (
            <RowAction key={action.key} action={action} />
          ))}
        </li>
      ))}
    </ul>
  );
}

/** Панель действий списка (прототип): padding 8 14, border-top line-hair, фон surface-sunken. */
export function ListToolbar({ children, note }: { children: ReactNode; note?: ReactNode }) {
  return (
    <div className="flex items-center gap-8 border-t border-line-hair bg-surface-sunken px-14 py-8 first:border-t-0">
      {note ? <span className="min-w-0 text-[12px] text-ink-500">{note}</span> : null}
      <span className="flex-1" />
      {children}
    </div>
  );
}

export interface ListToolbarButtonProps {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  /** Почему кнопка неактивна (подсказка). */
  disabledReason?: string;
}

/** Кнопка панели списка (прототип): 26, radius 16, белая, рамка line-strong, 12/500. */
export function ListToolbarButton({
  children,
  onClick,
  disabled,
  disabledReason,
}: ListToolbarButtonProps) {
  const button = (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex h-26 shrink-0 cursor-pointer items-center rounded-16 border border-line-strong bg-paper-pure px-10 text-[12px] font-medium whitespace-nowrap text-ink-900 hover:bg-paper-deep disabled:cursor-not-allowed disabled:text-ink-400 disabled:hover:bg-paper-pure"
    >
      {children}
    </button>
  );
  if (disabled && disabledReason) {
    return (
      <Tooltip content={disabledReason}>
        <span className="inline-flex shrink-0">{button}</span>
      </Tooltip>
    );
  }
  return button;
}
